import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { StaticVideo, CreateVideo, VideoType } from '@/types/video.types';
import { Session } from '@/types/session.types';
import ManageVodContentModal from './manage-vod-content-modal';
import AddVideoPopover from './add-video-popover';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectSessionByField } from '@/store/slices/project';
import { UploadVideo } from '@/connection/content/videos';
import { getActiveChannel } from '@/store/slices/user/selectors';
import SessionVodLinkInfo from './session-vod-link-info';
import SessionVodEmbedInfo from './session-vod-embed-info';
import SessionVodRecordedInfo from './session-vod-recorded-info';
import toastTrigger from '@/general-ui/toast/toast-trigger';
import OptionalComponent from '@ui/optional-component/optional-component';
import { selectRecordings, selectUploadedVideos } from '@/store/slices/content/selectors';
import { Case, SwitchCase } from '@ui/switch-case/switch-case';
import { EToastTypes } from '@ui/toast/toast.types';

type TSessionVodContentProps = {
  sessionVideo: StaticVideo | null;
  isEditMode: boolean;
  selectedType?: VideoType | null;
  session?: Session | null;
  projectDefaultLanguage?: string;
  onSetSelectedType?: (type: VideoType | null) => void;
  onReset?: () => void;
  showAddPopover?: boolean;
  setShowAddPopover?: Dispatch<SetStateAction<boolean>>;
  onModalOpenChange?: (open: boolean) => void;
};

const SessionVodContent = ({
  sessionVideo,
  isEditMode,
  selectedType = null,
  session,
  projectDefaultLanguage,
  onSetSelectedType,
  onReset,
  showAddPopover = false,
  setShowAddPopover,
  onModalOpenChange,
}: TSessionVodContentProps) => {
  const channel = useAppSelector(getActiveChannel);
  const recordings = useAppSelector(selectRecordings);
  const uploadedVideos = useAppSelector(selectUploadedVideos);

  const [manageVodContentModalOpen, setManageVodContentModalOpen] = useState(false);
  const [optimisticVideo, setOptimisticVideo] = useState<StaticVideo | null>(null);

  const dispatch = useAppDispatch();

  // use optimistic video if available, otherwise fall back to sessionVideo
  const displayVideo = isEditMode ? optimisticVideo || sessionVideo : sessionVideo;
  const videoType = selectedType || displayVideo?.type || null;

  const handlePopoverSelect = useCallback(
    (option: VideoType | null) => {
      if (option === VideoType.recorded) {
        setManageVodContentModalOpen(true);
        onModalOpenChange?.(true);
        onSetSelectedType?.(VideoType.recorded);
      } else {
        onSetSelectedType?.(option);
      }
      setShowAddPopover?.(false);
    },
    [onModalOpenChange, onSetSelectedType, setShowAddPopover]
  );

  const handleSaveVideo = useCallback(
    (videoId: string) => {
      if (!session || !projectDefaultLanguage) return;

      // find the video from recordings or uploaded videos for optimistic update
      const allVideos = [...(recordings || []), ...(uploadedVideos || [])];
      const selectedVideo = allVideos.find((video) => video.uuid === videoId);

      if (selectedVideo) {
        setOptimisticVideo(selectedVideo);
      }

      const updatedStaticVideos = {
        ...session.static_videos,
        [projectDefaultLanguage]: videoId,
      };

      dispatch(
        saveWorkingProjectSessionByField({
          uuid: session.uuid,
          static_videos: updatedStaticVideos,
        })
      );
    },
    [dispatch, session, projectDefaultLanguage, recordings, uploadedVideos]
  );

  const handleSaveLink = useCallback(
    async (linkUrl: string, videoName: string, thumbnailUrl?: string) => {
      if (!channel) return;

      const videoData: CreateVideo = {
        type: VideoType.url,
        name: videoName,
        source: linkUrl,
        use_thumbnail: !!thumbnailUrl,
        original_image: thumbnailUrl ?? '',
        image: thumbnailUrl ?? '',
      };

      try {
        const uploadedVideo = await UploadVideo(channel, videoData);

        handleSaveVideo(uploadedVideo.uuid);
      } catch (error) {
        console.error('Error uploading video link:', error);
        toastTrigger('There was an error saving the video. Please try again.', {
          type: EToastTypes.error,
        });
      }
    },
    [channel, handleSaveVideo]
  );

  const handleSaveEmbed = useCallback(
    async (embedCode: string, videoName: string) => {
      if (!channel) return;

      const videoData: CreateVideo = {
        type: VideoType.embed,
        name: videoName,
        source: embedCode,
        use_thumbnail: false,
        original_image: '',
        image: '',
      };

      try {
        const uploadedVideo = await UploadVideo(channel, videoData);
        handleSaveVideo(uploadedVideo.uuid);
      } catch (error) {
        console.error('Error uploading video link:', error);
        toastTrigger('There was an error saving the video. Please try again.', {
          type: EToastTypes.error,
        });
      }
    },
    [channel, handleSaveVideo]
  );

  const handleModalClose = useCallback(() => {
    setManageVodContentModalOpen(false);
    onModalOpenChange?.(false);
    if (!sessionVideo) {
      onSetSelectedType?.(null);
    }
  }, [sessionVideo, onModalOpenChange, onSetSelectedType]);

  // clear optimistic video when sessionVideo updates (API call completed)
  useEffect(() => {
    if (sessionVideo && optimisticVideo && sessionVideo.uuid === optimisticVideo.uuid) {
      setOptimisticVideo(null);
    }
  }, [sessionVideo, optimisticVideo]);

  // clear optimistic video when session changes (new project/session loaded)
  useEffect(() => {
    setOptimisticVideo(null);
  }, [session?.uuid]);

  useEffect(() => {
    if (!sessionVideo) {
      setOptimisticVideo(null);
    }
  }, [sessionVideo]);

  if (isEditMode && !displayVideo && !selectedType) {
    return <AddVideoPopover isUpdatingBroadcastType={false} onSelect={handlePopoverSelect} />;
  }

  if (!isEditMode && !sessionVideo) {
    return null;
  }

  const content = (
    <SwitchCase input={videoType}>
      <Case value={VideoType.url}>
        <SessionVodLinkInfo
          sessionVideo={displayVideo}
          isEditMode={isEditMode}
          onReset={isEditMode ? onReset : undefined}
          onSave={isEditMode ? handleSaveLink : undefined}
        />
      </Case>
      <Case value={VideoType.embed}>
        <SessionVodEmbedInfo
          sessionVideo={displayVideo}
          isEditMode={isEditMode}
          onReset={isEditMode ? onReset : undefined}
          onSave={isEditMode ? handleSaveEmbed : undefined}
        />
      </Case>
      <Case value={[VideoType.recorded, VideoType.uploaded]}>
        <SessionVodRecordedInfo
          sessionVideo={displayVideo!}
          isEditMode={isEditMode}
          onReset={isEditMode ? onReset : undefined}
        />
      </Case>
    </SwitchCase>
  );

  if (!isEditMode) {
    return content;
  }

  return (
    <>
      <OptionalComponent display={showAddPopover}>
        <AddVideoPopover isUpdatingBroadcastType={false} autoOpen onSelect={handlePopoverSelect} />
      </OptionalComponent>
      <OptionalComponent display={!showAddPopover && !manageVodContentModalOpen}>{content}</OptionalComponent>
      <ManageVodContentModal
        open={manageVodContentModalOpen}
        onClose={handleModalClose}
        onSave={handleSaveVideo}
        currentVideoId={sessionVideo?.uuid}
      />
    </>
  );
};

export default SessionVodContent;
